import React, { useContext, useState } from "react";
import Utils from "../../../Common/Utils";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AuthManager from "../../../Common/AuthManager";
import AppSettings from "../../../Settings/AppSettings";
import { AppContext } from "../../../Context/AppContext";
import { toast } from "Components/DesignSystem/Toast/useToast";
import Alert from "Components/DesignSystem/Alert/Alert";
import Button from "Components/DesignSystem/Button/Button";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Typography from "Components/DesignSystem/Typography/Typography";
import { CategorySearch, CategoryGrid } from "./CategoryRadioPicker";

/**
 * Component to bulk manage products for each categories
 * using xls sheets
 *
 * On the design system since § 126. The job-polling contract is untouched: a
 * recursive `setTimeout` at 1000ms that deliberately SURVIVES unmount (closing the
 * dialog does not stop the network traffic — see `okf/pdw/e2e.md` § 8).
 *
 * **`instanceId` is required.** `/gms/pdw` renders this component twice at once —
 * permanently on the page, and again inside the "Download → Product Data" dialog
 * (`okf/pdw/e2e.md` § 7). Both copies used to hardcode `name="optradio"` on their
 * radios and `id="iframe_pdw_download"` on their iframe, so the two instances
 * fought over one radio group and one DOM id. It is a required parameter, not a
 * defaulted one: a default is exactly how the two copies would silently collide
 * again.
 *
 * A `ModalContainer` wrapping `PDWTracker` used to live here. `setShowPopup(true)`
 * was never called from anywhere, so it could not open — removed as dead code, not
 * migrated.
 *
 * @param {string} instanceId - unique per mounted copy; scopes radio name, iframe id and testids
 * @param {array} categoryTypeList - category types
 * @param {object} categoriesList - {[categoryTypeId]: [categories]}
 */
const ReversePDW = ({ instanceId, categoriesList, categoryTypeList }) => {
  const api_base_url = AppSettings.GMS_BACKEND_API_URL;
  let appData = useContext(AppContext);
  let industryId =
    appData.get_selected_industry() && appData.get_selected_industry().id;
  const channelKey = appData?.channelKey;
  const [selectedCategory, setselectedCategory] = useState({
    id: null,
    name: "",
  });
  const [filteredList, setFilteredList] = useState({ ...categoriesList });
  let [searchString, setSearchString] = useState("");

  let [downloadUrl, setDownloadUrl] = React.useState(null);
  /*
    One status object rather than a bare server string (§ 127).

    `downloadStausLog` used to hold whatever `ret.data.status` said, and the banner
    decided its own tone by comparing that string to the literal `"completed"`. Two
    things followed from that, both bad:

      - The UI printed machine values. At the exact moment the file starts
        downloading, the most useful sentence available was the word "completed".
      - **An error rendered as the in-progress banner, forever.** `error: true` set
        the status to the failure text and raised a toast, but the banner's condition
        was `status !== null && status !== "completed"` — so a failed job showed a
        spinner that never stopped, saying "still working" about something that had
        already failed. `okf/pdw/e2e.md` recorded this as the poll-stuck spinner.

    `phase` is now explicit, so the banner cannot disagree with reality, and `detail`
    keeps the server's own words as supporting text — a stuck job stays diagnosable
    from a screenshot.
  */
  const [status, setStatus] = useState({ phase: 'idle', detail: null });

  async function do_download_pdw() {
    if(!channelKey)return
    setStatus({ phase: 'running', detail: null });
    setDownloadUrl(null);
    let newurl = `${api_base_url}pdw/reverse/${industryId}/${selectedCategory.id}/${channelKey}`;
    let res = await ServiceWrapper.doGet(
      newurl,
      null,
      null,
      AuthManager.getGmsToken()
    );
    setStatus({ phase: 'running', detail: "Making request" });
    setTimeout(() => {
      check_download_status(res.data.jobid);
    }, 1000);
  }

  async function check_download_status(jobid) {
    let ret = await ServiceWrapper.doGet(
      api_base_url + "job/status/" + jobid,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!ret.errorfound) {
      if (!ret.data.data) {
        if(ret.data?.processing){
          setStatus({ phase: 'running', detail: ret.data.status });
          setTimeout(() => {
            check_download_status(jobid);
          }, 1000);
        } else if(ret.data.error){
          setStatus({ phase: 'failed', detail: ret.data.status });
          toast.error(ret.data.status);
        } else {
          /* Neither processing, nor an error, nor any data — the job stopped in a
             state the API does not describe. `stopped`, not `failed`: claiming a
             failure we cannot substantiate is its own kind of wrong. What matters is
             that polling has ended, so the spinner must too. */
          setStatus({ phase: 'stopped', detail: ret.data.status });
          toast.info(ret.data.status);
        }
      } else {
        setDownloadUrl(ret.data.data);
        setStatus({ phase: 'completed', detail: null });
        /* No success toast. The banner sits directly under the button that was just
           pressed and already carries the progress; a toast in the corner saying the
           same word is a second announcement of one event. Error and info toasts
           stay — those are not duplicated. */
        setTimeout(() => {
          setDownloadUrl(null);
          setStatus({ phase: 'idle', detail: null });
          setselectedCategory({ id: null, name: null });
        }, 4000);
      }
    } else {
      /* `errorfound` used to fall straight through this `if` and do NOTHING: no
         state change, no toast, no further poll. The spinner from the previous
         render just stayed up permanently, with no indication anything had gone
         wrong at all. */
      setStatus({ phase: 'failed', detail: "Could not reach the job service." });
      toast.error("Could not reach the job service.");
    }
  }

  async function handleSearchAttribute(value) {
    setSearchString(value);
    if (!Utils.isEmpty(value)) {
      let searchval = value.toString().trim().toLowerCase();
      let filteredData = []
        .concat(
          ...Object.values(categoriesList).map((categoryTypeItems) => {
            return categoryTypeItems.filter((item) => {
              return (
                (item.name != null &&
                  item.name.toLowerCase().includes(searchval)) ||
                (item.description != null &&
                  item.description.toLowerCase().includes(searchval))
              );
            });
          })
        )
        .reduce((acc, category) => {
          (acc[category.cattype] = acc[category.cattype] || []).push(category);
          return acc;
        }, {});
      setFilteredList(filteredData);
    } else {
      setFilteredList(categoriesList);
    }
  }

  /*
    Headline first, the server's own words underneath. Tone follows `phase`, so the
    banner can no longer say "in progress" about a job that failed.
  */
  const STATUS_UI = {
    running:   { tone: 'info',    headline: 'Preparing your download\u2026', spinner: true },
    completed: { tone: 'success', headline: 'Your file is downloading' },
    failed:    { tone: 'danger',  headline: 'Download failed' },
    stopped:   { tone: 'warning', headline: "Download didn't finish" },
  };
  const statusUi = STATUS_UI[status.phase];

  return (
    <div data-testid={instanceId}>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-edge tw-pb-3">
        <div className="tw-min-w-0 tw-flex-1">
          <Typography className="tw-text-ink" variant="title-header" as="h2" data-testid={`${instanceId}-heading`}>
            Download Products
          </Typography>
          <Typography variant="body-primary" as="p" className="tw-text-ink-secondary tw-mt-1">
            Select category to download all products. To update changes in
            current products, update the downloaded XLS file and import to PDW.
          </Typography>
        </div>
        {/* Beside the heading, which is why the search is a separate export. */}
        <CategorySearch
          instanceId={instanceId}
          value={searchString}
          onChange={handleSearchAttribute}
          label="Search categories to download"
          className="tw-w-[280px] tw-shrink-0"
        />
      </div>

      <div className="tw-pb-2 tw-pt-4">
        <CategoryGrid
          instanceId={instanceId}
          categoryTypeList={categoryTypeList}
          filteredList={filteredList}
          selectedCategoryId={selectedCategory.id}
          onSelect={(item) =>
            setselectedCategory({ id: item.id, name: item.name })
          }
        />

        <div className="tw-mt-5">
          <Button
            variant="primary"
            id={`${instanceId}-download-btn`}
            data-testid={`${instanceId}-download-btn`}
            /* Also disabled while a job is in flight. It used to gate on the
               category alone, so a second click during the poll started a SECOND
               job — two `pdw/reverse` requests and two independent poll chains
               racing to write the same `downloadUrl`. */
            disabled={selectedCategory.id === null || status.phase === 'running'}
            onClick={() => {
              if (appData.pm.checkPermission("gmsproducts_read")) {
                do_download_pdw();
              } else {
                toast.info("You don't have permission to view the products");
              }
            }}
          >
            Download
          </Button>
        </div>

        {statusUi && (
          <div className="tw-mt-4">
            <Alert
              tone={statusUi.tone}
              live
              data-testid={`${instanceId}-status`}
            >
              <span className="tw-flex tw-items-start tw-gap-2">
                {statusUi.spinner && (
                  <Spinner size="sm" aria-hidden="true" className="tw-mt-0.5 tw-shrink-0" />
                )}
                <span className="tw-flex tw-flex-col">
                  <span>{statusUi.headline}</span>
                  {status.detail ? (
                    /* The raw server status, kept as secondary detail rather than as
                       the headline — someone screenshotting a stuck job still hands
                       support the value they need. */
                    <span
                      className="tw-text-ink-secondary"
                      data-testid={`${instanceId}-status-detail`}
                    >
                      {status.detail}
                    </span>
                  ) : null}
                </span>
              </span>
            </Alert>
          </div>
        )}

        {/*
          The hidden-iframe download trick, kept as-is. Its id is scoped by
          `instanceId`: `iframe_pdw_download` was hardcoded here, in PdwDownload, in
          Validate and in BulkAction, and at least two of those are live at once on
          this page.
        */}
        <iframe
          style={{ display: "none" }}
          src={downloadUrl}
          id={`${instanceId}-iframe`}
          title="Reverse PDW"
        />
      </div>
    </div>
  );
};
export default ReversePDW;
